/**
 * Hooks de **mutación** del dominio Pedidos de láminas (TanStack Query).
 *
 * Cubre el alta, la edición, el borrado y las transiciones de estado
 * (enviar/recibir/retirar-faltante). Tras éxito **invalidan las queries afectadas**
 * (`docs/frontend_conventions.md`, Datos): como una mutación mueve pedidos entre
 * listas del tablero (o los crea/borra), se invalida la raíz `['pedidos-laminas']`
 * (tablero + historial + detalles) en vez de una lista concreta. El gating de
 * permiso lo decide la UI antes de exponer la acción; aquí solo se ejecuta y se
 * propaga el `ApiError` (la pantalla muestra `{error_code, message}`).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import {
  createPedido,
  deletePedido,
  enviarPedido,
  recibirPedido,
  retirarFaltante,
  updatePedido,
} from "../api";
import type {
  PedidoCreateBody,
  PedidoDetail,
  PedidoUpdateBody,
} from "../types";
import { PEDIDOS_ROOT_KEY, pedidoDetailQueryKey } from "./queryKeys";

/** Crea un pedido único (`POST /pedidos-laminas`). También sirve a los faltantes. */
export function useCreatePedido() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean; message: string }, Error, PedidoCreateBody>({
    mutationFn: (body) => createPedido(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PEDIDOS_ROOT_KEY });
    },
  });
}

interface UpdateVariables {
  id: number;
  body: PedidoUpdateBody;
}

/** Edita un pedido pendiente (`PATCH /pedidos-laminas/<id>`, solo admin). */
export function useUpdatePedido() {
  const queryClient = useQueryClient();
  const sedeId = getActiveSedeId();
  return useMutation<PedidoDetail, Error, UpdateVariables>({
    mutationFn: ({ id, body }) => updatePedido(id, body),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: PEDIDOS_ROOT_KEY });
      void queryClient.invalidateQueries({
        queryKey: pedidoDetailQueryKey(sedeId, id),
      });
    },
  });
}

/** Envía un pedido (`POST /pedidos-laminas/<id>/enviar`, pendiente → en_camino). */
export function useEnviarPedido() {
  const queryClient = useQueryClient();
  return useMutation<PedidoDetail, Error, number>({
    mutationFn: (id) => enviarPedido(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PEDIDOS_ROOT_KEY });
    },
  });
}

/** Recibe un pedido (`POST /pedidos-laminas/<id>/recibir`, en_camino → recibido). */
export function useRecibirPedido() {
  const queryClient = useQueryClient();
  return useMutation<PedidoDetail & { inv_actualizado?: boolean }, Error, number>({
    mutationFn: (id) => recibirPedido(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PEDIDOS_ROOT_KEY });
    },
  });
}

/** Elimina un pedido pendiente (`DELETE /pedidos-laminas/<id>`, solo admin). */
export function useEliminarPedido() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deletePedido(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PEDIDOS_ROOT_KEY });
    },
  });
}

/** Retira un aviso de faltante (`POST /pedidos-laminas/<id>/retirar-faltante`). */
export function useRetirarFaltante() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => retirarFaltante(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PEDIDOS_ROOT_KEY });
    },
  });
}
